import React, { FC, useEffect, useState } from 'react';
import BalanceStore from '../../stores/BalanceStore';
import ModalStore from '../../stores/ModalStore';
import LoadingStore from '../../stores/LoadingStore';
import CMSStore from '../../stores/content-stores/CMSStore';
import CustomModal from '../../common-components/CustomModal';
import { ReactComponent as BigWarningCross } from '../../layout/icons/white-cross-in-red-circle-big.svg';
import { ReactComponent as WarningCross } from '../../layout/icons/white-cross-in-red-circle.svg';
import BalanceSettlementModal from './modals/BalanceSettlementModal';
import useIsMobileView from '../../hooks/useIsMobileView';

interface FrisbiiEmbeddedProps {
  sessionUrl: string;
  balanceStore: BalanceStore;
  customerId: string;
  isNegativeBalance: boolean;
  modalStore: ModalStore;
  loadingStore: LoadingStore;
  cmsStore: CMSStore;
}

export type FrisbiiEvent = {
  customer: string;
  error: string;
  id: string;
  invoice: string;
  payment_method: string;
  subscribtion: string;
};

const FrisbiiEmbedded: FC<FrisbiiEmbeddedProps> = (props) => {
  const {
    sessionUrl,
    balanceStore,
    customerId,
    isNegativeBalance,
    modalStore,
    loadingStore,
    cmsStore
  } = props;
  const [isFrisbiiScriptLoaded, setIsFrisbiiScriptLoaded] = useState(false);
  const [scriptLoadFailed, setScriptLoadFailed] = useState(false);
  const isMobileView = useIsMobileView();

  const outerWrapperStyle: React.CSSProperties = {
    ...(isMobileView ? {} : { width: '375px' }),
    marginInline: 'auto'
  };

  const extractSessionId = (url: string): string => {
    const match = url.match(/cs_[a-f0-9]+/);
    return match ? match[0] : url;
  };

  const showSuccessModal = () => {
    const { paymentCardStore, manageUserAccessStore, transactionsStore, paymentStore } =
      cmsStore.rootStore;
    const { notificationStore } = cmsStore.rootStore;
    const handleClose = async () => {
      modalStore.closeModal();
      loadingStore.enableGlobalSpinner();
      try {
        await balanceStore.getBalance(customerId);
        balanceStore.clearSettlement();
        notificationStore.getNotifications();
        if (manageUserAccessStore.isPrivateCustomer()) {
          await transactionsStore.getAccountTransactions();
        } else {
          const { filterStore } = cmsStore.rootStore;
          filterStore.invoiceFilterStore.resetInvoiceFilters();
          await Promise.all([paymentStore.getInvoices(), paymentStore.getCardPayments()]);
        }
      } finally {
        loadingStore.disableGlobalSpinner();
      }
    };
    modalStore.openModal(
      <BalanceSettlementModal
        cmsStore={cmsStore}
        balanceStore={balanceStore}
        paymentCardStore={paymentCardStore}
        onClose={handleClose}
      />
    );
  };

  const handleSuccess = async (data?: FrisbiiEvent) => {
    try {
      if (isNegativeBalance && !data?.payment_method) {
        // eslint-disable-next-line no-console
        console.error('Payout requires payment_method but Reepay did not provide one');
        loadingStore.disableGlobalSpinner();
        const errorHeader = cmsStore.getContent('SettlementErrorModal')?.content;
        const errorText = cmsStore.getContent('SettlementErrorModal')?.subText;
        modalStore.openModal(
          <CustomModal
            topIconDesktop={<WarningCross />}
            topIconMobile={<BigWarningCross />}
            isHeaderRed
            header={errorHeader}
            contentText={errorText}
            primaryButtonText={cmsStore.getContent('Ok')?.content}
            onPrimaryButtonClick={() => modalStore.closeModal()}
            closable
          />
        );
        return;
      }

      modalStore.closeModal();
      loadingStore.enableGlobalSpinner();

      const processBody: { paymentMethodId?: string; test: boolean } = { test: false };
      if (isNegativeBalance && data?.payment_method) {
        processBody.paymentMethodId = data.payment_method;
      }
      await balanceStore.processBalanceSettlement(customerId, processBody);

      await balanceStore.pollSettlementStatus(customerId);

      loadingStore.disableGlobalSpinner();
      showSuccessModal();
    } catch (error) {
      loadingStore.disableGlobalSpinner();
      // eslint-disable-next-line no-console
      console.error('Settlement failed:', error);
      modalStore.closeModal();
      const errorHeader = cmsStore.getContent('SettlementProcessErrorModal')?.content;
      const errorText = cmsStore.getContent('SettlementProcessErrorModal')?.subText;
      modalStore.openModal(
        <CustomModal
          topIconDesktop={<WarningCross />}
          topIconMobile={<BigWarningCross />}
          isHeaderRed
          header={errorHeader}
          contentText={errorText}
          primaryButtonText={cmsStore.getContent('Ok')?.content}
          onPrimaryButtonClick={() => modalStore.closeModal()}
          closable
        />
      );
    }
  };

  const handleCancel = async () => {
    try {
      await balanceStore.cancelBalanceSettlement(customerId);
      modalStore.closeModal();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to cancel settlement:', error);
      modalStore.closeModal();
    }
  };

  useEffect(() => {
    if (window.Reepay) {
      setIsFrisbiiScriptLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.reepay.com/checkout.js';
    script.addEventListener('load', () => setIsFrisbiiScriptLoaded(true));
    script.addEventListener('error', () => {
      setScriptLoadFailed(true);
      modalStore.closeModal();
      const defaultHeader = cmsStore.getContent('SettlementErrorModal')?.content;
      const defaultText = cmsStore.getContent('SettlementErrorModal')?.subText;
      modalStore.openModal(
        <CustomModal
          topIconDesktop={<WarningCross />}
          topIconMobile={<BigWarningCross />}
          isHeaderRed
          header={defaultHeader}
          contentText={defaultText}
          primaryButtonText={cmsStore.getContent('Ok')?.content}
          onPrimaryButtonClick={() => modalStore.closeModal()}
          closable
        />
      );
    });
    document.body.appendChild(script);

    // eslint-disable-next-line consistent-return
    return () => {
      if (script.parentNode) {
        document.body.removeChild(script);
      }
    };
  }, [modalStore, cmsStore]);

  useEffect(() => {
    if (!isFrisbiiScriptLoaded || !sessionUrl || !window.Reepay) {
      return;
    }

    let rp: any = null;

    try {
      const reepay = window.Reepay;
      const sessionId = extractSessionId(sessionUrl);

      rp = new reepay.EmbeddedCheckout(sessionId, {
        html_element: 'frisbii_container'
      });

      rp.addEventHandler(reepay.Event.Accept, (data: FrisbiiEvent) => {
        handleSuccess(data);
      });

      rp.addEventHandler(reepay.Event.Close, () => {
        handleCancel();
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to initialize Frisbii checkout:', error);
      modalStore.closeModal();
      const errorHeader = cmsStore.getContent('SettlementSessionErrorModal')?.content;
      const errorText = cmsStore.getContent('SettlementSessionErrorModal')?.subText;
      modalStore.openModal(
        <CustomModal
          topIconDesktop={<WarningCross />}
          topIconMobile={<BigWarningCross />}
          isHeaderRed
          header={errorHeader}
          contentText={errorText}
          primaryButtonText={cmsStore.getContent('Ok')?.content}
          onPrimaryButtonClick={() => modalStore.closeModal()}
          closable
        />
      );
    }
  }, [
    isFrisbiiScriptLoaded,
    sessionUrl,
    customerId,
    balanceStore,
    modalStore,
    loadingStore,
    cmsStore
  ]);

  if (scriptLoadFailed) {
    return (
      <div style={{ ...outerWrapperStyle, padding: '20px', textAlign: 'center' }}>
        {cmsStore.getContent('SettlementErrorModal')?.subText}
      </div>
    );
  }

  return (
    <div style={outerWrapperStyle}>
      <div style={{ height: '460px' }} id="frisbii_container" />
    </div>
  );
};

export default FrisbiiEmbedded;
